import { useEffect, useMemo, useState } from "react"
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DateRange } from "../../../types"
import { useNavigate } from "react-router-dom"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import { BinaryOperator, Query } from "@cubejs-client/core"
import useDate from "~/ui-rtk/hooks/date"
import CreativeReportCard from "~/ui-rtk/pages/BrandMedia/CreativeReportsV2/components/CreativeReportCard"
import { useResolveAssetsMutation } from "~/ui-rtk/api/assetsApi"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { cacheAssetUrl } from "~/ui-rtk/app/slices/asset-cache.slice"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import type { AssetSourceType } from "~/ui-rtk/components/atoms/MarathonImage"
import {
  ALWAYS_METRICS,
  DEFAULT_METRICS,
  CREATIVE_REPORTS_METRICS_MAP,
  TMetricsMapKey,
  BRAND_METRICS,
  REVENUE_METRICS,
  ENGAGEMENT_METRICS,
  PERFORMANCE_METRICS,
  ALL_METRICS,
} from "~/ui-rtk/pages/BrandMedia/CreativeReportsV2/connect"
import { Loader } from "~/ui-rtk/components/ui/common"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import MultiSelectWithChips, {
  TMultiSelectWithChipsOption,
  TMultiSelectWithChipsOptionGroup,
} from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import SortByControl from "~/ui-rtk/components/ui/controls/SortBy/SortBy"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"
import { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"

const dayjs = useDate()

type AdCreativeReportingSectionProps = {
  dateRange: DateRange
  isLoading?: boolean
  className?: string
}

// Define metric groups
const METRIC_GROUPS: TMultiSelectWithChipsOptionGroup[] = [
  { label: "Brand Metrics", value: "brand" },
  { label: "Revenue Metrics", value: "revenue" },
  { label: "Platform Engagements", value: "engagements" },
  { label: "Performance Metrics", value: "performance" },
]

// Helper function to get group for a metric
const getMetricGroup = (metric: TMetricsMapKey): string => {
  if (BRAND_METRICS.includes(metric)) return "brand"
  if (REVENUE_METRICS.includes(metric)) return "revenue"
  if (ENGAGEMENT_METRICS.includes(metric)) return "engagements"
  if (PERFORMANCE_METRICS.includes(metric)) return "performance"
  return "performance"
}

// Build options from the metrics map
const CREATIVE_REPORTS_METRICS_OPTIONS = Object.keys(
  CREATIVE_REPORTS_METRICS_MAP,
).map(
  (key: string) =>
    ({
      label: CREATIVE_REPORTS_METRICS_MAP[key as TMetricsMapKey],
      value: key,
      group: getMetricGroup(key as TMetricsMapKey),
    }) as TMultiSelectWithChipsOption,
)

const CREATIVE_REPORTS_METRICS_OPTIONS_MAP = Object.keys(
  CREATIVE_REPORTS_METRICS_MAP,
).reduce(
  (acc: Record<TMetricsMapKey, TMultiSelectWithChipsOption>, key: string) => ({
    ...acc,
    [key]: {
      label: CREATIVE_REPORTS_METRICS_MAP[key as TMetricsMapKey],
      value: key,
      group: getMetricGroup(key as TMetricsMapKey),
    } as TMultiSelectWithChipsOption,
  }),
  {} as Record<TMetricsMapKey, TMultiSelectWithChipsOption>,
)

const DEFAULT_SELECTED_METRICS: TMetricsMapKey[] = [
  ...ALWAYS_METRICS,
  ...DEFAULT_METRICS.slice(0, 3),
]

// Common sortable metrics for the sort by dropdown
const SORTABLE_METRICS: TMetricsMapKey[] = [
  "spend",
  "brand_value_agg",
  "brand_value_over_spend",
  "purchase_value",
  "purchase_value_over_spend",
  "ga4_total_revenue",
  "ga4_total_revenue_over_spend",
  "total_value",
  "purchases",
  "reach",
  "likes",
  "comments",
  "shares",
  "distinct_ad_count",
]

export default function AdCreativeReportingSection({
  dateRange,
  isLoading: externalLoading = false,
  className,
}: AdCreativeReportingSectionProps) {
  const navigate = useNavigate()
  const { depVar, depVarScaler } = useDepVar()
  const dispatch = useAppDispatch()
  const cachedUrls = useAppSelector(state => state.assetCacheSlice.urls)
  const [resolveAssetsMutation] = useResolveAssetsMutation()

  // State for selected metrics
  const [selectedMetrics, setSelectedMetrics] = useState<TMetricsMapKey[]>(
    DEFAULT_SELECTED_METRICS,
  )

  // State for sorting (default to spend desc)
  const [sorting, setSorting] = useState<TSortingState>({
    id: "spend",
    desc: true,
  })

  const selectedMetricsOptions: TMultiSelectWithChipsOption[] = useMemo(
    () =>
      selectedMetrics.map(id => ({
        ...CREATIVE_REPORTS_METRICS_OPTIONS_MAP[id],
        permanent: ALWAYS_METRICS.includes(id),
      })),
    [selectedMetrics],
  )

  const alwaysOptions = useMemo(
    () => ALWAYS_METRICS.map(id => CREATIVE_REPORTS_METRICS_OPTIONS_MAP[id]),
    [],
  )

  const removableOptions = useMemo(
    () =>
      CREATIVE_REPORTS_METRICS_OPTIONS.filter(
        option => !ALWAYS_METRICS.includes(option.value as TMetricsMapKey),
      ),
    [],
  )

  const selectOptions = useMemo(
    () => [
      ...alwaysOptions.map(option => ({
        ...option,
        permanent: true,
      })),
      ...removableOptions,
    ],
    [alwaysOptions, removableOptions],
  )

  const handleMetricsChange = (values: TMultiSelectWithChipsOption[]) => {
    const metrics = values.map(({ value }) => value as TMetricsMapKey)
    setSelectedMetrics([...new Set([...ALWAYS_METRICS, ...metrics])])
  }

  // Column config for sort by dropdown
  const columnConfig = useMemo<TableVisualizationColumn[]>(
    () =>
      SORTABLE_METRICS.map(metric => ({
        key: metric,
        header: CREATIVE_REPORTS_METRICS_MAP[metric],
        type: "number" as const,
      })),
    [],
  )

  // Build cube query for top 10 creatives - fetch ALL metrics upfront
  const cubeQuery = useMemo(() => {
    const prefix = "company_media_metrics_ads_creative_report"

    // Fetch all available metrics so they're instantly available when user selects them
    const allMeasures = [
      ...ALL_METRICS.map(metric => `${prefix}.${metric}`),
      `${prefix}.creative_id_agg`,
    ]

    const q: Query = {
      dimensions: [`${prefix}.media_channel`, `${prefix}.root_ad_name`],
      measures: allMeasures,
      order: [[`${prefix}.${sorting.id}`, sorting.desc ? "desc" : "asc"]],
      limit: 10,
    }

    const filters = [
      {
        dimension: `${prefix}.dep_var`,
        operator: "equals" as BinaryOperator,
        values: [depVar],
      },
    ]

    if (depVarScaler.length > 0) {
      filters.push({
        dimension: `${prefix}.dep_var_scaler`,
        operator: "equals" as BinaryOperator,
        values: depVarScaler,
      })
    }

    const dateRangeWithStarts: [Date, Date] = [
      dayjs(dateRange.start).startOf("day").toDate(),
      dayjs(dateRange.end).endOf("day").toDate(),
    ]

    return polyfillCubeQueryWithTimeDimension(
      {
        ...q,
        timeDimensions: [
          {
            dimension: `${prefix}.md_date`,
          },
        ],
        filters,
      },
      {
        dateRange: dateRangeWithStarts,
      },
    )
  }, [dateRange, depVar, depVarScaler, sorting])

  const { resultSet, isLoading: cubeLoading } = useCubeQuery(cubeQuery)

  const isLoading = externalLoading || cubeLoading

  // Process result data
  const creatives = useMemo(() => {
    if (!resultSet) return []

    const prefix = "company_media_metrics_ads_creative_report"

    return resultSet.rawData().map(item => {
      const noPrefixItem: Record<string, string> = {}
      Object.keys(item).forEach(keyWithPrefix => {
        const keyNoPrefix = keyWithPrefix.replace(`${prefix}.`, "")
        noPrefixItem[keyNoPrefix] = String(item[keyWithPrefix] ?? "")
      })
      return noPrefixItem
    })
  }, [resultSet])

  // Bulk resolve assets for creative images (same approach as CreativeReportsV2)
  useEffect(() => {
    if (!creatives.length) return

    const uncached: Array<{
      sourceType: AssetSourceType
      externalRefId: string
      url?: string
    }> = []

    for (const item of creatives) {
      const creativeId = item.creative_id_agg
      const channel = item.media_channel
      if (!creativeId || !channel) continue

      const sourceType = mapMediaChannelToSourceType(String(channel))
      const key = `${sourceType}:${creativeId}`
      if (cachedUrls[key]) continue

      const url = item.md_image_url || item.thumbnail_url || undefined
      uncached.push({
        sourceType,
        externalRefId: String(creativeId),
        ...(url && { url }),
      })
    }

    if (uncached.length === 0) return

    void resolveAssetsMutation({ assets: uncached })
      .unwrap()
      .then(result => {
        for (const [key, asset] of Object.entries(result.results)) {
          if (!asset) continue

          let resolvedUrl =
            asset.syncStatus === "SYNCED"
              ? asset.url || asset.thumbnailUrl || null
              : null

          // Fall back to the original platform URL while asset is downloading
          if (!resolvedUrl) {
            const match = uncached.find(
              a => `${a.sourceType}:${a.externalRefId}` === key,
            )
            resolvedUrl = match?.url || null
          }

          if (resolvedUrl) {
            dispatch(cacheAssetUrl({ key, url: resolvedUrl }))
          }
        }
      })
  }, [creatives])

  const handleSeeMore = () => {
    navigate(brandRoutes.CREATIVE_REPORTS.V2)
  }

  return (
    <div
      className={cn(
        "rounded-2xl overflow-hidden",
        "bg-gradient-to-br from-system-blue-800/60 to-system-blue-900/80",
        "border border-system-blue-600/40",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-system-blue-600/30">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-white">
            Ad Creative Reporting
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <SortByControl
            columnConfig={columnConfig}
            sortBy={sorting}
            onSort={setSorting}
            align="right"
            className="h-10 rounded-lg"
          />
          <button
            onClick={handleSeeMore}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg",
              "bg-system-brand-pink-600/20 hover:bg-system-brand-pink-600/30",
              "text-system-brand-pink-400 hover:text-system-brand-pink-300",
              "border border-system-brand-pink-600/30 hover:border-system-brand-pink-500/50",
              "transition-all duration-200",
              "text-sm font-medium h-10",
            )}
          >
            See More
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Metrics Selection Bar */}
      <div className="px-6 py-3 border-b border-system-blue-600/30 bg-system-blue-800/30">
        <MultiSelectWithChips
          label="Metrics"
          options={selectOptions}
          className="h-system-2xl cursor-pointer"
          groups={METRIC_GROUPS}
          showTypeAhead={true}
          value={selectedMetricsOptions}
          onChange={handleMetricsChange}
        />
      </div>

      {/* Grid of Ad Creatives */}
      <div className="p-6">
        {(() => {
          if (isLoading) {
            return (
              <div className="h-64 flex items-center justify-center">
                <Loader size={40} />
              </div>
            )
          }
          if (creatives.length === 0) {
            return (
              <div className="h-64 flex items-center justify-center text-system-gray-400">
                No creative data available for this date range
              </div>
            )
          }
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {creatives.map((card, idx) => {
                const creativeId = card.creative_id_agg
                const channel = card.media_channel
                const cacheKey =
                  creativeId && channel
                    ? `${mapMediaChannelToSourceType(channel)}:${creativeId}`
                    : null
                const imgUrl =
                  (cacheKey && cachedUrls[cacheKey]) ||
                  card.md_image_url ||
                  card.thumbnail_url

                return (
                  <CreativeReportCard
                    key={`${card.root_ad_name}-${idx}`}
                    card={card}
                    primaryIdKey="root_ad_name"
                    metricsNames={selectedMetrics}
                    imgUrl={imgUrl}
                    disabledPillTypes={["campaign", "adset", "ad"]}
                  />
                )
              })}
            </div>
          )
        })()}
      </div>
    </div>
  )
}
